import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Modal, Form } from 'react-bootstrap';
import { AiOutlineFileAdd } from "react-icons/ai";
import { BsPencilFill, BsFileEarmarkCode } from "react-icons/bs";
import { LuFilter, LuX } from 'react-icons/lu';
import { GrInbox, GrAttachment } from "react-icons/gr";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { IoSettingsOutline } from "react-icons/io5";
import axios from 'axios';
import { FaRegHandPaper } from "react-icons/fa";

// Define the API base URL from environment variables or default to localhost
const API_URL = process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

// Function to retrieve userLabId from localStorage by searching various keys and structures
const getUserLabId = () => {
  try {
    // List of common authentication-related keys to check first
    const authKeys = ['user', 'auth', 'userData', 'currentUser', 'profile'];
    
    for (const key of authKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          const parsed = JSON.parse(value);
          // Check for labId in different possible nested properties
          const labId = parsed.labId || parsed.userLabId || parsed.lab_id || 
                        parsed.lab?.id || parsed.lab?.labId || 
                        parsed.user?.labId || parsed.user?.userLabId;
          
          // Validate if the found labId matches the expected pattern (000000 followed by two digits)
          if (labId && /^000000\d{2}$/.test(labId)) {
            return labId;
          }
        } catch (e) {
          // Ignore if the value is not valid JSON
        }
      }
    }
    
    // If not found in auth keys, search all localStorage items
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      
      // Skip null or undefined values
      if (!value) continue;
      
      // Check if the value directly matches the labId pattern
      if (/^000000\d{2}$/.test(value.trim())) {
        return value.trim();
      }
      
      // Attempt to parse the value as JSON and search recursively
      try {
        const parsed = JSON.parse(value);
        
        // Recursive function to find labId within the parsed object
        const findLabId = (obj, path = '') => {
          if (!obj || typeof obj !== 'object') return null;
          
          for (const prop in obj) {
            const val = obj[prop];
            const currentPath = path ? `${path}.${prop}` : prop;
            
            // Check if the property value is a string matching the labId pattern
            if (typeof val === 'string' && /^000000\d{2}$/.test(val)) {
              return val;
            }
            
            // Recurse if the value is an object
            if (val && typeof val === 'object') {
              const found = findLabId(val, currentPath);
              if (found) return found;
            }
          }
          return null;
        };
        
        const foundId = findLabId(parsed);
        if (foundId) {
          return foundId;
        }
      } catch (e) {
        // Ignore if not valid JSON
      }
    }
    
    // Return null if no labId is found
    return null;
    
  } catch (error) {
    return null;
  }
};

// Main component for displaying and managing open cases
const OpenCases = () => {
  // State for storing all cases fetched from the API
  const [allCases, setAllCases] = useState([]); 
  // State for storing cases after applying filters
  const [filteredCases, setFilteredCases] = useState([]); 
  // State for storing account data
  const [accounts, setAccounts] = useState([]);
  // State for managing dropdown visibility for each case
  const [dropdownVisible, setDropdownVisible] = useState(null);
  // State for the current selected month (0-11)
  const [currentMonth, setCurrentMonth] = useState(11); // December
  // State for the current selected year
  const [currentYear, setCurrentYear] = useState(2025); // 2025
  // State for showing/hiding the filter modal
  const [showFilterModal, setShowFilterModal] = useState(false);
  // State for the total amount of filtered cases
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  // State for loading indicator
  const [loading, setLoading] = useState(true);
  // State for error messages
  const [error, setError] = useState(null);
  // State to track if fetch has been attempted
  const [fetchAttempted, setFetchAttempted] = useState(false);
  // State for the user's lab ID
  const [userLabId, setUserLabId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId, accountName } = location.state || {};

  // State for managing various filters
  const [filters, setFilters] = useState({
    filterType: 'account',
    filterValue: '',
    by: 'month',
    caseTag: 'any',
    properties: 'all',
    startDate: '',
    endDate: '',
    caseStatus: ''
  });

  // State for dynamic filter options derived from cases
  const [filterOptions, setFilterOptions] = useState({
    accounts: [],
    providers: [],
    patients: [],
    caseIds: [],
    deliveryDates: [],
    caseTags: [],
    statuses: []
  });

  // Function to format invoice numbers by padding with zeros
  const formatInvoiceNumber = (number) => {
    return number ? String(number).padStart(6, '0') : 'N/A';
  };

  // Function to format dates in 'en-GB' locale
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      return '';
    }
  };

  // Function to format amounts with two decimal places
  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Function to extract unique filter options from case data
  const extractFilterOptions = (casesData) => {
    
    const accounts = new Set();
    const providers = new Set();
    const patients = new Set();
    const caseIds = new Set();
    const deliveryDates = new Set();
    const caseTags = new Set();
    const statuses = new Set();

    casesData.forEach(caseItem => {
      if (caseItem.account_name && caseItem.account_name !== 'No account assigned') {
        accounts.add(caseItem.account_name);
      }
      if (caseItem.provider_name && caseItem.provider_name !== 'No provider assigned') {
        providers.add(caseItem.provider_name);
      }
      if (caseItem.last_name) {
        patients.add(caseItem.last_name);
      } else if (caseItem.first_name) {
        patients.add(caseItem.first_name);
      }
      if (caseItem.caseNumber) {
        caseIds.add(caseItem.caseNumber);
      } else if (caseItem.case_id) {
        caseIds.add(caseItem.case_id);
      }
      if (caseItem.delivery_date) {
        const formattedDate = formatDate(caseItem.delivery_date);
        if (formattedDate) {
          deliveryDates.add(formattedDate);
        }
      }
      if (caseItem.tags && Array.isArray(caseItem.tags)) {
        caseItem.tags.forEach(tag => caseTags.add(tag));
      }
      if (caseItem.status) {
        statuses.add(caseItem.status);
      }
    });

    const options = {
      accounts: Array.from(accounts).sort().map(account => ({ value: account, label: account })),
      providers: Array.from(providers).sort().map(provider => ({ value: provider, label: provider })),
      patients: Array.from(patients).sort().map(patient => ({ value: patient, label: patient })),
      caseIds: Array.from(caseIds).sort().map(caseId => ({ value: caseId, label: caseId })),
      deliveryDates: Array.from(deliveryDates).sort().map(date => ({ value: date, label: date })),
      caseTags: Array.from(caseTags).sort(),
      statuses: Array.from(statuses).sort()
    };

    return options;
  };

  // Effect to retrieve and set userLabId on component mount
  useEffect(() => {
    const labId = getUserLabId();
    setUserLabId(labId);
  }, []);

  // Effect to fetch accounts based on userLabId
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        // Skip fetch if no userLabId is available
        if (!userLabId) {
          return;
        }

        const response = await axios.get(`${API_URL}/api/account/lab-accounts/${userLabId}`);
        const accountsData = response.data.data || [];
        
        // Format the fetched accounts data
        const formattedAccounts = accountsData.map((account) => ({
          _id: account._id,
          name: account.account_name,
          accountId: account.accountId,
          account_type: account.account_type,
          city: account.city,
          zip: account.zip,
          address: account.address,
          telephone: account.telephone,
          address2: account.address2,
          email: account.email,
          country: account.country,
          website: account.website,
          state: account.state,
          time_zone: account.time_zone,
          status: account.status,
          ddxEnable: account.ddxEnable,
          createdAt: account.createdAt,
          updatedAt: account.updatedAt,
        }));
        
        setAccounts(formattedAccounts);
      } catch (error) {
        setAccounts([]);
      }
    };

    if (userLabId) {
      fetchAccounts();
    }
  }, [userLabId]);

  // Effect to fetch cases from the API based on userLabId and accountId
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setFetchAttempted(true);
        
        // Prepare query parameters for the API request
        const params = {};
        
        if (userLabId) {
          params.userLabId = userLabId;
        }
        
        if (accountId) {
          params.accountId = accountId;
        }
        
        // Fetch cases from the API
        const response = await axios.get(`${API_URL}/api/cases/cases`, {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          params: params
        });
        
        if (response.data.success) {
          const casesData = response.data.data || [];
          
          // Set all cases and initial filter options
          setAllCases(casesData);
          
          const initialFilterOptions = extractFilterOptions(casesData);
          setFilterOptions(initialFilterOptions);
          
        } else {
          setError(response.data.message || 'Failed to load cases.');
        }
      } catch (error) {
        if (error.response) {
          setError(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          setError('No response from server. Please check your connection.');
        } else {
          setError('Failed to load cases. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userLabId, accountId]);

  // Helper function to extract labId from a case item
  const getCaseLabId = (caseItem) => {
    return caseItem.userLabId || 
           caseItem.addedBy?.labId || 
           caseItem.addedBy?.userLabId || 
           caseItem.labId;
  };

  // Function to filter cases by userLabId
  const filterByLabId = (casesToFilter) => {
    if (!userLabId) return casesToFilter;
    
    const labFiltered = casesToFilter.filter(caseItem => {
      const caseLabId = getCaseLabId(caseItem);
      return caseLabId === userLabId;
    });
    
    return labFiltered;
  };

  // Function to apply all active filters to the cases
  const applyAllFilters = (casesToFilter) => {
    
    // First, filter by labId
    let filtered = filterByLabId(casesToFilter);
    
    // Apply additional filters
    filtered = filtered.filter((caseItem) => {
      // Exclude cases with invoiceNumber (closed cases)
      if (caseItem.invoiceNumber) {
        return false;
      }
      
      // Exclude canceled cases
      if (caseItem.canceled === true || caseItem.status === 'canceled') {
        return false;
      }
      
      // Apply date-based filtering
      if (filters.by === 'month') {
        const caseDate = new Date(caseItem.date || caseItem.createdAt);
        if (isNaN(caseDate.getTime())) {
          return false;
        }
        
        const caseMonth = caseDate.getMonth();
        const caseYear = caseDate.getFullYear();
        
        if (caseMonth !== currentMonth || caseYear !== currentYear) {
          return false;
        }
      } else if (filters.by === 'dateRange') {
        const caseDate = new Date(caseItem.date || caseItem.createdAt);
        if (isNaN(caseDate.getTime())) {
          return false;
        }
        if (filters.startDate) {
          const startDate = new Date(filters.startDate);
          if (caseDate < startDate) {
            return false;
          }
        }
        if (filters.endDate) {
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (caseDate > endDate) {
            return false;
          }
        }
      }
      
      // Apply main filter based on selected type
      if (filters.filterValue) {
        switch (filters.filterType) {
          case 'account':
            if (caseItem.account_name !== filters.filterValue) {
              return false;
            }
            break;
          case 'provider':
            if (caseItem.provider_name !== filters.filterValue) {
              return false;
            }
            break;
          case 'patient':
            const patientName = caseItem.last_name || caseItem.first_name;
            if (patientName !== filters.filterValue) {
              return false;
            }
            break;
          case 'caseId':
            const caseId = caseItem.caseNumber || caseItem.case_id;
            if (caseId !== filters.filterValue) {
              return false;
            }
            break;
          case 'delivery':
            if (formatDate(caseItem.delivery_date) !== filters.filterValue) {
              return false;
            }
            break;
          default:
            break;
        }
      }
      
      // Apply case tag filter
      if (filters.caseTag !== 'any' && (!caseItem.tags || !caseItem.tags.includes(filters.caseTag))) {
        return false;
      }
      
      // Apply case status filter
      if (filters.caseStatus && caseItem.status !== filters.caseStatus) {
        return false;
      }
      
      // Apply properties filter (digital, enclosures, etc.)
      if (filters.properties !== 'all') {
        const hasDigital = caseItem.procedures?.some(p => p.category === 'digital');
        const hasEnclosures = caseItem.procedures?.some(p => p.category === 'enclosure');
        if (filters.properties === 'digital' && !hasDigital) {
          return false;
        }
        if (filters.properties === 'enclosures' && !hasEnclosures) {
          return false;
        }
        if (filters.properties === 'both' && (!hasDigital || !hasEnclosures)) {
          return false;
        }
      }
      
      return true;
    });
    
    return filtered;
  };

  // Effect to update filtered cases and filter options when dependencies change
  useEffect(() => {
    if (allCases.length === 0) {
      setFilteredCases([]);
      return;
    }

    // Apply filters to get updated filtered cases
    const newlyFilteredCases = applyAllFilters(allCases);
    setFilteredCases(newlyFilteredCases);
    
    // Update filter options based on filtered cases
    const newFilterOptions = extractFilterOptions(newlyFilteredCases);
    setFilterOptions(newFilterOptions);
    
  }, [allCases, filters, currentMonth, currentYear, userLabId]);

  // Effect to calculate total amount from filtered cases
  useEffect(() => {
    const total = filteredCases.reduce((sum, caseItem) => {
      const amount = caseItem.totalPrice || caseItem.totalAmount || 0;
      return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
    }, 0);
    setMonthlyTotal(total);
  }, [filteredCases]);

  // Function to get current filter options based on filter type
  const getCurrentFilterOptions = () => {
    switch (filters.filterType) {
      case 'account':
        return filterOptions.accounts;
      case 'provider':
        return filterOptions.providers;
      case 'patient':
        return filterOptions.patients;
      case 'caseId':
        return filterOptions.caseIds;
      case 'delivery':
        return filterOptions.deliveryDates;
      default:
        return [];
    }
  };

  // Handler for changing filter values
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    // Reset filter value if filter type changes
    if (name === 'filterType') {
      setFilters(prev => ({
        ...prev,
        filterType: value,
        filterValue: ''
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handler to apply filters and close modal
  const handleApplyFilters = () => {
    setShowFilterModal(false);
  };

  // Handler to reset all filters to default
  const handleResetFilters = () => {
    setFilters({
      filterType: 'account',
      filterValue: '',
      by: 'month',
      caseTag: 'any',
      properties: 'all',
      startDate: '',
      endDate: '',
      caseStatus: ''
    });
  };

  // Handler to toggle dropdown for a specific case
  const toggleDropdown = (caseId) => {
    setDropdownVisible(dropdownVisible === caseId ? null : caseId);
  };

  // Handler to navigate to the previous month
  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setDropdownVisible(null);
  };

  // Handler to navigate to the next month
  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setDropdownVisible(null);
  };

  // Handler for clicking on an account name to navigate to details
  const handleAccountClick = (accountName) => {
    const account = accounts.find(acc => acc.name === accountName);
    if (account) {
      navigate(`/tabsWorking/${account.accountId}`, {
        state: {
          accountData: account,
        },
      });
    }
  };

  // Get the name of the current month
  const monthName = new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long' });

  // State for toggling display of all cases (for debugging, but unused in production)
  const [showAllCases, setShowAllCases] = useState(false);

  // Render loading spinner if data is loading
  if (loading) {
    return (
      <div style={{ marginTop: '20px', width: '95%', margin: '0 auto', padding: "20px" }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span style={{ marginLeft: '10px' }}>
            {userLabId ? `Loading cases for lab: ${userLabId}` : 'Loading all cases...'}
          </span>
        </div>
      </div>
    );
  }

  // Render error message if there's an error
  if (error) {
    return (
      <div style={{ marginTop: '20px', width: '95%', margin: '0 auto', padding: "20px" }}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Cases</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            {userLabId ? `Lab ID: ${userLabId}` : 'Viewing all labs (no specific lab selected)'}
          </p>
        </div>
      </div>
    );
  }

  // Render the main component UI
  return (
    <div style={{ marginTop: '20px',  margin: '0 auto', padding: "20px" }}>
      {/* Header section with title and navigation buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 className="title" style={{ margin: 0, backgroundColor: "white" }}>
          {accountName
            ? `Open Cases for ${accountName}`
            : `Open Cases ${monthName} ${currentYear} (${formatAmount(monthlyTotal)})`}
          <br />
          <small style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>
            {userLabId ? `Lab: ${userLabId} | Showing ${filteredCases.length} of ${allCases.length} cases` : 'Viewing all labs'}
          </small>
        </h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handlePreviousMonth} style={{ padding: '10px', backgroundColor: '#004aad', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
            <MdChevronLeft style={{ fontSize: "20px" }} /> Previous Month
          </button>
          <button onClick={handleNextMonth} style={{ padding: '10px', backgroundColor: '#004aad', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
            Next Month <MdChevronRight style={{ fontSize: "20px" }} />
          </button>
        </div>
      </div>

      {/* Filter button to open modal */}
      <div className="col-md-0 d-flex justify-content-end">
        <button
          style={{ padding: '5px', backgroundColor: '#004aad', color: 'white', width: '90px', height: '35px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          onClick={() => setShowFilterModal(true)}
        >
          <LuFilter /> Filters
        </button>
      </div>
      <br /><br />

      {/* Table to display filtered cases */}
      <div style={{ width: "100%", margin: "0 auto", padding: "20px", border: "3px solid #f4f4f4", borderRadius: "5px", overflowX: 'auto' }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: '800px' }}>
          <thead>
            <tr>
              <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}><input type="checkbox" /></th>
              {["Inbox", "Attachment", "Case/Pan", "Invoice", "Account", "Provider", "Patient/Appt", "Account Ship", "Lab Ship", "Delivery Date", "On Hold Status"].map(header => (
                <th key={header} style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd", fontWeight: "bold", whiteSpace: 'nowrap' }}>
                  {header === "Inbox" ? <GrInbox style={{ fontSize: "20px" }} /> : header === "Attachment" ? <GrAttachment style={{ fontSize: "20px" }} /> : header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredCases.length > 0 ? (
              filteredCases.map(caseItem => {
                const caseNumber = caseItem.caseNumber || caseItem.case_id;
                const isOnHold = caseItem.status !== 'in progress';
                
                return (
                  <tr key={caseItem._id} style={{ borderBottom: "1px solid #ddd" }}>
                    <td style={{ padding: "10px", textAlign: "left" }}><input type="checkbox" /></td>
                    <td style={{ padding: "10px", textAlign: "left" }}>
                      {isOnHold ? <FaRegHandPaper style={{ fontSize: "20px" }} /> : <GrInbox style={{ fontSize: "20px" }} />}
                    </td>
                    <td style={{ padding: "10px", textAlign: "left" }}><GrAttachment style={{ fontSize: "20px" }} /></td>
                    <td style={{ padding: "10px", textAlign: "left", position: "relative" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Link to={`/case/${caseNumber}`} style={{ textDecoration: "none", color: "#004aad", fontSize: "17px" }}>
                              #{caseNumber}
                            </Link>
                            <button
                              style={{ fontSize: "18px", background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
                              onClick={(e) => { e.stopPropagation(); toggleDropdown(caseItem._id); }}
                            >
                              <IoSettingsOutline style={{ color: "#004aad", fontSize: "22px" }} />
                            </button>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <p style={{ margin: 0 }}>Pan Color:</p>
                            <span
                              style={{
                                width: "12px",
                                height: "12px",
                                backgroundColor: caseItem.color ? caseItem.color.toLowerCase() : "#ccc",
                                display: "inline-block",
                                borderRadius: "3px",
                              }}
                            ></span>
                            <span>{caseItem.color || "N/A"}</span>
                          </div>
                          {caseItem.procedures && caseItem.procedures.length > 0 ? (
                            <div style={{ fontSize: "14px" }}>
                              <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                                {caseItem.procedures.map((proc, index) => (
                                  <li key={index} style={{ marginBottom: '5px' }}>
                                    {proc.procedure}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <div style={{ fontSize: "14px", color: "#666" }}>No procedures selected</div>
                          )}
                        </div>
                      </div>
                      {dropdownVisible === caseItem._id && (
                        <div style={{ position: "absolute", top: "100%", left: "50px", backgroundColor: "#fff", border: "1px solid #ccc", padding: "10px", borderRadius: "5px", zIndex: 1000, width: "150px", boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)" }}>
                          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                            <li style={{ padding: "8px 0", cursor: "pointer" }}><BsFileEarmarkCode /> Close/Invoice</li>
                            <li style={{ padding: "8px 0", cursor: "pointer" }}><BsPencilFill /> Add Notes</li>
                            <li style={{ padding: "8px 0", cursor: "pointer" }}><AiOutlineFileAdd /> Upload Files</li>
                          </ul>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "10px", textAlign: "left", whiteSpace: 'nowrap' }}>
                      {caseItem.invoiceNumber ? (
                        <Link
                          to={`/invoices/${formatInvoiceNumber(caseItem.invoiceNumber)}`}
                          state={{ caseId: caseNumber }}
                          style={{ textDecoration: "none", color: "#004aad", fontSize: "16px", display: 'inline-block' }}
                        >
                          #{formatInvoiceNumber(caseItem.invoiceNumber)}
                        </Link>
                      ) : (
                        "No Invoice"
                      )}
                      {(caseItem.totalPrice || caseItem.totalAmount) &&
                       typeof (caseItem.totalPrice || caseItem.totalAmount) === 'number' ? (
                        <div style={{ marginTop: '1px', color: '#555', fontSize: '14px' }}>
                          {formatAmount(caseItem.totalPrice || caseItem.totalAmount)}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: "10px", textAlign: "left" }}>
                      <span
                        onClick={() => handleAccountClick(caseItem.account_name)}
                        style={{
                          textDecoration: "none",
                          color: "#004aad",
                          fontSize: "16px",
                          cursor: "pointer",
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {caseItem.account_name || "N/A"}
                      </span>
                    </td>
                    <td style={{ padding: "10px", textAlign: "left" }}>{caseItem.provider_name || "N/A"}</td>
                    <td style={{ padding: "10px", textAlign: "left" }}>
                      {caseItem.last_name || caseItem.first_name || "N/A"}
                    </td>
                    <td style={{ padding: "10px", textAlign: "left" }}>
                      {caseItem.start_date ? new Date(caseItem.start_date).toLocaleDateString() : "N/A"}
                    </td>
                    <td style={{ padding: "10px", textAlign: "left" }}>
                      {caseItem.arrive_date ? new Date(caseItem.arrive_date).toLocaleDateString() : "N/A"}
                    </td>
                    <td style={{ padding: "10px", textAlign: "left" }}>
                      {caseItem.delivery_date ? new Date(caseItem.delivery_date).toLocaleDateString() : "N/A"}
                    </td>
                    <td style={{ padding: "10px", textAlign: "left" }}>{caseItem.status || "N/A"}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="12" style={{ textAlign: "center", padding: "20px" }}>
                  {allCases.length === 0 ? (
                    <div>
                      <p>No cases found in the database.</p>
                     
                    </div>
                  ) : (
                    <div>
                      <p>No open cases match the current filters.</p>
                      <p style={{ fontSize: '14px', color: '#666' }}>
                        <strong>Note:</strong> You have {allCases.length} total cases in database.
                        <br />
                        {userLabId && `Cases matching Lab ID ${userLabId}: ${filterByLabId(allCases).length}`}
                        <br />
                        <strong>Try this:</strong> 
                        <br />1. Click "Next Month" until you see "December 2025"
                        <br />2. Or click "Filters" and change Date Range to "Custom Date Range"
                        <br />3. Or reset filters using the "Reset" button in filter modal
                      </p>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <br />

      {/* Pagination buttons (placeholder, not functional in this code) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', width: '100%' }}>
        {["First", "Previous", "1", "Next", "Last"].map(label => (
          <Button key={label} style={{ backgroundColor: label === "1" ? "#004aad" : "#f0f0f0", borderColor: label === "1" ? "#004aad" : "#ced4da", flex: 1, color: label === "1" ? "white" : "black", borderRadius: '0', margin: '0 2px' }}>
            {label}
          </Button>
        ))}
      </div>

      {/* Modal for configuring filters */}
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)} size="lg" centered>
        <Modal.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
          <Modal.Title style={{ fontWeight: 'bold', color: '#004aad' }}>Change Filters</Modal.Title>
          <button onClick={() => setShowFilterModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem' }}>
            <LuX />
          </button>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
              <div style={{ flex: '1', minWidth: '250px' }}>
                <h5 style={{ color: '#004aad', marginBottom: '15px' }}>Filters</h5>
                <Form.Group controlId="filterType" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Filter By</Form.Label>
                  <Form.Select
                    name="filterType"
                    value={filters.filterType}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="account">Account</option>
                    <option value="provider">Provider</option>
                    <option value="patient">Patient</option>
                    <option value="caseId">Case ID</option>
                    <option value="delivery">Delivery Date</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group controlId="filterValue" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>
                    {filters.filterType === 'account' ? 'Account' :
                     filters.filterType === 'provider' ? 'Provider' :
                     filters.filterType === 'patient' ? 'Patient' :
                     filters.filterType === 'caseId' ? 'Case ID' : 'Delivery Date'}
                  </Form.Label>
                  <Form.Select
                    name="filterValue"
                    value={filters.filterValue}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                    disabled={getCurrentFilterOptions().length === 0}
                  >
                    <option value="">All {filters.filterType === 'account' ? 'Accounts' :
                                      filters.filterType === 'provider' ? 'Providers' :
                                      filters.filterType === 'patient' ? 'Patients' :
                                      filters.filterType === 'caseId' ? 'Case IDs' : 'Delivery Dates'} 
                                      ({getCurrentFilterOptions().length})
                    </option>
                    {getCurrentFilterOptions().map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                  {getCurrentFilterOptions().length === 0 && (
                    <Form.Text className="text-muted">
                      No {filters.filterType === 'account' ? 'accounts' :
                           filters.filterType === 'provider' ? 'providers' :
                           filters.filterType === 'patient' ? 'patients' :
                           filters.filterType === 'caseId' ? 'case IDs' : 'delivery dates'} found in filtered cases
                    </Form.Text>
                  )}
                </Form.Group>
                <Form.Group controlId="filterBy" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Date Range</Form.Label>
                  <Form.Select
                    name="by"
                    value={filters.by}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="month">Current Month ({monthName} {currentYear})</option>
                    <option value="dateRange">Custom Date Range</option>
                  </Form.Select>
                </Form.Group>
                {filters.by === 'dateRange' && (
                  <>
                    <Form.Group controlId="filterStartDate" style={{ marginBottom: '15px' }}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="startDate"
                        value={filters.startDate}
                        onChange={handleFilterChange}
                        style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                      />
                    </Form.Group>
                    <Form.Group controlId="filterEndDate">
                      <Form.Label style={{ fontWeight: 'bold' }}>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="endDate"
                        value={filters.endDate}
                        onChange={handleFilterChange}
                        style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                      />
                    </Form.Group>
                  </>
                )}
              </div>
              <div style={{ flex: '1', minWidth: '250px' }}>
                <h5 style={{ color: '#004aad', marginBottom: '15px' }}>Case Status Options</h5>
                <Form.Group controlId="filterCaseTag" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Case Tag</Form.Label>
                  <Form.Select
                    name="caseTag"
                    value={filters.caseTag}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                    disabled={filterOptions.caseTags.length === 0}
                  >
                    <option value="any">Any ({filterOptions.caseTags.length} tags available)</option>
                    {filterOptions.caseTags.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </Form.Select>
                  {filterOptions.caseTags.length === 0 && (
                    <Form.Text className="text-muted">No case tags found in filtered cases</Form.Text>
                  )}
                </Form.Group>
                <Form.Group controlId="filterCaseStatus" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Case Status</Form.Label>
                  <Form.Select
                    name="caseStatus"
                    value={filters.caseStatus}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                    disabled={filterOptions.statuses.length === 0}
                  >
                    <option value="">All Statuses ({filterOptions.statuses.length} available)</option>
                    {filterOptions.statuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </Form.Select>
                  {filterOptions.statuses.length === 0 && (
                    <Form.Text className="text-muted">No statuses found in filtered cases</Form.Text>
                  )}
                </Form.Group>
                <Form.Group controlId="filterProperties" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Properties</Form.Label>
                  <Form.Select
                    name="properties"
                    value={filters.properties}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="all">All</option>
                    <option value="digital">Digital Manufacturing Files</option>
                    <option value="enclosures">Enclosures</option>
                    <option value="both">Digital + Enclosures</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
            <div className="alert alert-info">
              <small>
                <strong>Filter Info:</strong> Showing filter options from {filteredCases.length} filtered cases.
                <br />
                <strong>Accounts:</strong> {filterOptions.accounts.length} | 
                <strong> Providers:</strong> {filterOptions.providers.length} | 
                <strong> Patients:</strong> {filterOptions.patients.length} | 
                <strong> Statuses:</strong> {filterOptions.statuses.length}
              </small>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #dee2e6', padding: '15px' }}>
          <Button variant="secondary" onClick={handleResetFilters} style={{ borderRadius: '5px', padding: '8px 15px' }}>
            Reset All Filters
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyFilters}
            style={{ backgroundColor: '#004aad', borderColor: '#004aad', borderRadius: '5px', padding: '8px 15px' }}
          >
            Apply Filters
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OpenCases;